import { useMutation } from '@tanstack/react-query'
import { type ErrorWithBody, post, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import { type DuplicateContactCheckResponse } from '../../PersonalDetails/Hooks'
import type { AccountHolderAddress, ApplicationResponse } from '@mobi/api-types'
import { useLocation } from 'react-router'
import { trackSignUpError } from '../Utils'

export type TypeSignupApplication = {
  firstName: string
  middleName: string
  surname: string
  birthdate: string
  email: string
  mobileNumber: string
  applicationReferenceNumber: number
  enquiryId: string
  residentialAddress?: AccountHolderAddress
  previousAddress?: AccountHolderAddress
  marketingOptInIndicator: boolean
  termsAndConditionsText?: string
  termsAndConditionsConsent?: boolean
  firstNameAlias: string
  lastNameAlias: string
}

export type TypeSignUpApplicationRequest = TypeSignupApplication & {
  affiliateMarketingId?: string
  locationId?: string
  venueId?: string
}

export type TypeSignUpApplicationResponse = {
  applicationReferenceNumber: number
  enquiryId: string
  duplicateExists: boolean
  isDuplicateClosed: boolean
  isVerified: boolean
  unVerifiedReason: string
  potentialFraudMatch: boolean
  potentialSanctionedMatch: boolean
}

type UseSignUpApplicationOptions = {
  setValidationMessages: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setShowErrorPopup: React.Dispatch<React.SetStateAction<boolean>>
}

export const useSignUpApplication = ({
  setValidationMessages,
  setShowErrorPopup,
}: UseSignUpApplicationOptions) => {
  const { pathname } = useLocation()
  const { mutate: saveSignUpApplication, status: saveSignUpApplicationStatus } = useMutation<
    TypeSignUpApplicationResponse | ApplicationResponse,
    ErrorWithBody<DuplicateContactCheckResponse>,
    TypeSignUpApplicationRequest
  >({
    mutationFn: applicationDetails =>
      post<
        TypeSignUpApplicationRequest,
        TypeSignUpApplicationResponse | ApplicationResponse,
        ErrorWithBody<DuplicateContactCheckResponse>
      >(SignUpUrl.signUpApplication, applicationDetails),
    mutationKey: [queryKeys.signupApplicationSave],
    onError: error => {
      if (error.status === 409 && error.body) {
        if (error.body.isDuplicateMobile) {
          trackSignUpError(pathname, 'Duplicate Mobile Number')
          setValidationMessages(prev => ({ ...prev, mobile: 'duplicate' }))
        }
        if (error.body.isDuplicateEmail) {
          trackSignUpError(pathname, 'Duplicate Email')
          setValidationMessages(prev => ({ ...prev, email: 'duplicate' }))
        }
        return
      }
      setShowErrorPopup(true)
      trackSignUpError(pathname, error.statusText)
    },
  })
  return { saveSignUpApplication, saveSignUpApplicationStatus }
}
